import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {filter, first} from 'rxjs';
import {AuthenticationService} from 'src/app/auth/auth.service';
import {selectRouterParam} from 'src/app/core/store/router/router.selector';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';

@Component({
  selector: 'user-request-email',
  templateUrl: './user-request-email.component.html',
  styleUrls: ['./user-request-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, OphLoadingModule, OphIconModule],
})
export class UserRequestEmailComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly store$ = inject(Store);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly passwordInputType = signal<'password' | 'text'>('password');
  readonly passwordConfirmInputType = signal<'password' | 'text'>('password');
  readonly view = signal<'valid' | 'invalid' | 'passwordChanged' | null>(null);
  readonly loadingValid = signal<boolean>(true);
  readonly loadingReset = signal<boolean>(false);
  readonly errorMessage = signal<string>('');

  readonly form = this.fb.group({
    password: ['', Validators.required],
    confirmPassword: ['', Validators.required],
  });

  private readonly token$ = this.store$.select(selectRouterParam('token'));

  ngOnInit(): void {
    this.subscribeToToken();
  }

  private subscribeToToken(): void {
    this.token$
      .pipe(
        filter(token => !!token),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(token => {
        // Success is added to url on success of password reset
        if (token === 'success') {
          return;
        }

        this.authenticationService
          .validateUserRequestToken(token)
          .pipe(first())
          .subscribe({
            next: data => {
              this.errorMessage.set('');
              this.view.set(data.valid ? 'valid' : 'invalid');
              this.loadingValid.set(false);
            },
            error: error => {
              this.errorMessage.set(error.error);
              this.view.set('invalid');
              this.loadingValid.set(false);
            },
          });
      });
  }

  onTogglePassword(event: MouseEvent): void {
    event.preventDefault();
    const type = this.passwordInputType() === 'password' ? 'text' : 'password';
    this.passwordInputType.set(type);
  }

  onToggleConfirmPassword(event: MouseEvent): void {
    event.preventDefault();
    const type = this.passwordConfirmInputType() === 'password' ? 'text' : 'password';
    this.passwordConfirmInputType.set(type);
  }

  onGo(): void {
    const passwordValue = this.passwordControl?.value;
    const confirmPasswordValue = this.confirmPasswordControl?.value;

    if (passwordValue !== confirmPasswordValue) {
      this.errorMessage.set('Passwords must match');
      return;
    }

    if (!passwordValue) {
      this.errorMessage.set('Password is required');
      return;
    }

    this.token$.pipe(first()).subscribe(token => {
      this.errorMessage.set('');
      this.loadingReset.set(true);

      this.authenticationService
        .resetPassword(token, passwordValue, 'userRequest')
        .pipe(first())
        .subscribe({
          next: () => {
            this.errorMessage.set('');
            this.router.navigate(['reset-password', 'success']);
            this.view.set('passwordChanged');
            this.loadingValid.set(false);
            this.loadingReset.set(false);
          },
          error: () => {
            this.loadingValid.set(false);
            this.loadingReset.set(false);
          },
        });
    });
  }

  onLogin(event: MouseEvent): void {
    event.preventDefault();
    this.router.navigate(['login']);
  }

  onRequestNewLink(): void {
    this.router.navigate(['login'], {
      queryParams: {view: 'forgotPassword'},
      queryParamsHandling: 'merge',
    });
  }

  get passwordControl() {
    return this.form.get('password');
  }

  get confirmPasswordControl() {
    return this.form.get('confirmPassword');
  }
}
